import { supabase } from "@/integrations/supabase/client";
import { Client, ClientFormData } from "@/types";
import { sanitizeUuid } from "@/utils/uuidUtils";

export class ClientService {
  /**
   * Get all clients for the current repair shop
   */
  static async getClients(repairShopId: string): Promise<Client[]> {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("repair_shop_id", repairShopId)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching clients:", error);
      throw new Error(`Failed to fetch clients: ${error.message}`);
    }

    return data.map(this.mapDatabaseToClient);
  }

  /**
   * Get a single client by ID
   */
  static async getClientById(id: string): Promise<Client | null> {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        return null; // Client not found
      }
      console.error("Error fetching client:", error);
      throw new Error(`Failed to fetch client: ${error.message}`);
    }

    return this.mapDatabaseToClient(data);
  }

  /**
   * Create a new client
   */
  static async createClient(
    clientData: ClientFormData,
    repairShopId: string,
    createdBy: string
  ): Promise<Client> {
    const sanitizedData = {
      name: clientData.name.trim(),
      phone: clientData.phone?.trim() || null,
      email: clientData.email?.trim() || null,
      address: clientData.address?.trim() || null,
      notes: clientData.notes?.trim() || null,
      credit_limit: clientData.creditLimit || 0,
      current_credit_balance: 0, // New clients start with zero balance
      is_active: clientData.isActive ?? true,
      repair_shop_id: repairShopId,
    };

    const { data, error } = await supabase
      .from("clients")
      .insert(sanitizedData)
      .select()
      .single();

    if (error) {
      console.error("Error creating client:", error);
      throw new Error(`Failed to create client: ${error.message}`);
    }

    return this.mapDatabaseToClient(data);
  }

  /**
   * Update an existing client
   */
  static async updateClient(
    id: string,
    clientData: Partial<ClientFormData>
  ): Promise<void> {
    const sanitizedData: any = {};

    if (clientData.name !== undefined) {
      sanitizedData.name = clientData.name.trim();
    }
    if (clientData.phone !== undefined) {
      sanitizedData.phone = clientData.phone?.trim() || null;
    }
    if (clientData.email !== undefined) {
      sanitizedData.email = clientData.email?.trim() || null;
    }
    if (clientData.address !== undefined) {
      sanitizedData.address = clientData.address?.trim() || null;
    }
    if (clientData.notes !== undefined) {
      sanitizedData.notes = clientData.notes?.trim() || null;
    }
    if (clientData.creditLimit !== undefined) {
      sanitizedData.credit_limit = clientData.creditLimit;
    }
    if (clientData.isActive !== undefined) {
      sanitizedData.is_active = clientData.isActive;
    }

    sanitizedData.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from("clients")
      .update(sanitizedData)
      .eq("id", id);

    if (error) {
      console.error("Error updating client:", error);
      throw new Error(`Failed to update client: ${error.message}`);
    }
  }

  /**
   * Delete a client (soft delete by setting inactive)
   */
  static async deleteClient(id: string): Promise<void> {
    const { error } = await supabase
      .from("clients")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("Error deleting client:", error);
      throw new Error(`Failed to delete client: ${error.message}`);
    }
  }

  /**
   * Search clients by name, phone, or email
   */
  static async searchClients(
    repairShopId: string,
    searchTerm: string
  ): Promise<Client[]> {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("repair_shop_id", repairShopId)
      .or(
        `name.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
      )
      .order("name", { ascending: true });

    if (error) {
      console.error("Error searching clients:", error);
      throw new Error(`Failed to search clients: ${error.message}`);
    }

    return data.map(this.mapDatabaseToClient);
  }

  /**
   * Get clients with outstanding credit balances
   */
  static async getClientsWithCredit(repairShopId: string): Promise<Client[]> {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("repair_shop_id", repairShopId)
      .gt("current_credit_balance", 0)
      .order("current_credit_balance", { ascending: false });

    if (error) {
      console.error("Error fetching clients with credit:", error);
      throw new Error(`Failed to fetch clients with credit: ${error.message}`);
    }

    return data.map(this.mapDatabaseToClient);
  }

  /**
   * Update client credit balance
   */
  static async updateCreditBalance(
    id: string,
    newBalance: number
  ): Promise<void> {
    const { error } = await supabase
      .from("clients")
      .update({
        current_credit_balance: newBalance,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error("Error updating client credit balance:", error);
      throw new Error(`Failed to update credit balance: ${error.message}`);
    }
  }

  /**
   * Get client invoice history summary
   */
  static async getClientInvoiceSummary(clientId: string) {
    const { data, error } = await supabase
      .from("invoices")
      .select("id, total_amount, payment_status, created_at")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching client invoice summary:", error);
      throw new Error(`Failed to fetch invoice summary: ${error.message}`);
    }

    const totalInvoices = data.length;
    const totalAmount = data.reduce(
      (sum, invoice) => sum + invoice.total_amount,
      0
    );
    const paidInvoices = data.filter(
      (invoice) => invoice.payment_status === "paid"
    ).length;
    const unpaidAmount = data
      .filter((invoice) => invoice.payment_status !== "paid")
      .reduce((sum, invoice) => sum + invoice.total_amount, 0);

    return {
      totalInvoices,
      totalAmount,
      paidInvoices,
      unpaidInvoices: totalInvoices - paidInvoices,
      unpaidAmount,
      recentInvoices: data.slice(0, 5), // Last 5 invoices
    };
  }

  /**
   * Map database record to Client interface
   */
  private static mapDatabaseToClient(dbRecord: any): Client {
    return {
      id: dbRecord.id,
      name: dbRecord.name,
      phone: dbRecord.phone,
      email: dbRecord.email,
      address: dbRecord.address,
      notes: dbRecord.notes,
      creditLimit: dbRecord.credit_limit || 0,
      currentCreditBalance: dbRecord.current_credit_balance || 0,
      isActive: dbRecord.is_active ?? true,
      repairShopId: dbRecord.repair_shop_id,
      createdAt: new Date(dbRecord.created_at),
      updatedAt: new Date(dbRecord.updated_at),
    };
  }

  /**
   * Validate client data
   */
  static validateClientData(data: ClientFormData): string[] {
    const errors: string[] = [];

    if (!data.name || data.name.trim().length === 0) {
      errors.push("Client name is required");
    }

    if (data.name && data.name.trim().length > 255) {
      errors.push("Client name must be less than 255 characters");
    }

    if (data.email && !this.isValidEmail(data.email)) {
      errors.push("Invalid email format");
    }

    if (data.phone && data.phone.length > 50) {
      errors.push("Phone number must be less than 50 characters");
    }

    if (data.creditLimit && data.creditLimit < 0) {
      errors.push("Credit limit cannot be negative");
    }

    return errors;
  }

  /**
   * Calculate and update client credit balance based on unpaid invoices
   */
  static async recalculateClientCreditBalance(
    clientId: string
  ): Promise<number> {
    console.log(
      "ClientService: Recalculating credit balance for client",
      clientId
    );

    // Get all unpaid and partially paid invoices for this client
    const { data: invoices, error: invoicesError } = await supabase
      .from("invoices")
      .select("id, total_amount, payment_status")
      .eq("client_id", clientId)
      .in("payment_status", ["unpaid", "partial", "overdue"]);

    console.log("ClientService: Found invoices", invoices);

    if (invoicesError) {
      console.error("Error fetching client invoices:", invoicesError);
      throw new Error(`Failed to fetch invoices: ${invoicesError.message}`);
    }

    // Calculate total unpaid amount
    let totalUnpaidAmount = 0;
    for (const invoice of invoices || []) {
      if (
        invoice.payment_status === "unpaid" ||
        invoice.payment_status === "overdue"
      ) {
        // Full invoice amount is unpaid
        totalUnpaidAmount += invoice.total_amount;
      } else if (invoice.payment_status === "partial") {
        // Calculate remaining amount for partial payments
        const paidAmount = await this.getInvoicePaidAmount(invoice.id);
        const remainingAmount = invoice.total_amount - paidAmount;
        totalUnpaidAmount += Math.max(0, remainingAmount);
      }
    }

    // Update client's credit balance
    console.log("ClientService: Updating credit balance to", totalUnpaidAmount);
    await this.updateCreditBalance(clientId, totalUnpaidAmount);
    console.log("ClientService: Credit balance updated successfully");
    return totalUnpaidAmount;
  }

  /**
   * Get the total paid amount for an invoice
   */
  static async getInvoicePaidAmount(invoiceId: string): Promise<number> {
    const { data: payments, error } = await supabase
      .from("payment_records")
      .select("amount")
      .eq("invoice_id", invoiceId);

    if (error) {
      console.error("Error fetching payment records:", error);
      return 0;
    }

    return payments?.reduce((total, payment) => total + payment.amount, 0) || 0;
  }

  /**
   * Update client credit balance based on unpaid invoices
   */
  static async updateClientCreditFromInvoices(clientId: string): Promise<void> {
    try {
      // Calculate total unpaid amount for this client
      const { data: unpaidInvoices, error: invoiceError } = await supabase
        .from("invoices")
        .select("total_amount, payment_status, paidAmount:total_paid")
        .eq("client_id", clientId)
        .neq("payment_status", "paid")
        .neq("status", "cancelled");

      if (invoiceError) {
        throw new Error(
          `Failed to fetch unpaid invoices: ${invoiceError.message}`
        );
      }

      // Calculate total unpaid amount
      const totalUnpaidAmount = unpaidInvoices.reduce((total, invoice) => {
        const paidAmount = invoice.paidAmount || 0;
        const remainingAmount = invoice.total_amount - paidAmount;
        return total + Math.max(0, remainingAmount);
      }, 0);

      // Update client's credit balance
      await this.updateCreditBalance(clientId, totalUnpaidAmount);
    } catch (error) {
      console.error("Error updating client credit from invoices:", error);
      throw error;
    }
  }

  /**
   * Create a credit transaction record
   */
  static async createCreditTransaction(
    clientId: string,
    transactionType: "credit" | "payment" | "adjustment",
    amount: number,
    referenceType?: "sale" | "invoice" | "manual" | "payment",
    referenceId?: string,
    description?: string,
    paymentMethod?: string,
    repairShopId?: string,
    createdBy?: string
  ): Promise<void> {
    try {
      // Get current client balance
      const client = await this.getClientById(clientId);
      if (!client) {
        throw new Error("Client not found");
      }

      let newBalance = client.currentCreditBalance;

      // Calculate new balance based on transaction type
      switch (transactionType) {
        case "credit":
          newBalance += amount; // Increase debt
          break;
        case "payment":
          newBalance -= amount; // Decrease debt
          break;
        case "adjustment":
          newBalance = amount; // Set to specific amount
          break;
      }

      // Ensure balance doesn't go negative
      newBalance = Math.max(0, newBalance);

      // Create transaction record
      const { error: transactionError } = await supabase
        .from("credit_transactions")
        .insert({
          client_id: clientId,
          transaction_type: transactionType,
          amount: amount,
          balance_after: newBalance,
          reference_type: referenceType,
          reference_id: referenceId,
          description: description,
          payment_method: paymentMethod,
          repair_shop_id: repairShopId || client.repairShopId,
          created_by: createdBy,
        });

      if (transactionError) {
        throw new Error(
          `Failed to create credit transaction: ${transactionError.message}`
        );
      }

      // Update client balance
      await this.updateCreditBalance(clientId, newBalance);
    } catch (error) {
      console.error("Error creating credit transaction:", error);
      throw error;
    }
  }

  /**
   * Get credit transaction history for a client
   */
  static async getCreditTransactions(clientId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from("credit_transactions")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching credit transactions:", error);
      throw new Error(`Failed to fetch credit transactions: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Sync all client credit balances with their unpaid invoices
   * This should be run periodically to ensure data consistency
   */
  static async syncAllClientCredits(repairShopId: string): Promise<void> {
    try {
      const clients = await this.getClients(repairShopId);

      for (const client of clients) {
        await this.updateClientCreditFromInvoices(client.id);
      }
    } catch (error) {
      console.error("Error syncing client credits:", error);
      throw error;
    }
  }

  /**
   * Check if client has exceeded their credit limit
   */
  static async checkCreditLimit(clientId: string): Promise<{
    withinLimit: boolean;
    currentBalance: number;
    creditLimit: number;
    availableCredit: number;
  }> {
    const client = await this.getClientById(clientId);
    if (!client) {
      throw new Error("Client not found");
    }

    const availableCredit = Math.max(
      0,
      client.creditLimit - client.currentCreditBalance
    );
    const withinLimit = client.currentCreditBalance <= client.creditLimit;

    return {
      withinLimit,
      currentBalance: client.currentCreditBalance,
      creditLimit: client.creditLimit,
      availableCredit,
    };
  }

  /**
   * Get clients who have exceeded their credit limit
   */
  static async getClientsOverCreditLimit(
    repairShopId: string
  ): Promise<Client[]> {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("repair_shop_id", repairShopId)
      .eq("is_active", true)
      .gt("current_credit_balance", 0)
      .order("current_credit_balance", { ascending: false });

    if (error) {
      console.error("Error fetching clients over credit limit:", error);
      throw new Error(
        `Failed to fetch clients over credit limit: ${error.message}`
      );
    }

    return data
      .filter((client) => client.current_credit_balance > client.credit_limit)
      .map(this.mapDatabaseToClient);
  }

  /**
   * Simple email validation
   */
  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
